import React, { useEffect, useMemo, useRef, useState } from "react";
import { TabulatorFull as Tabulator } from "tabulator-tables";

import {
  getHotelServices,
  getHotelProducts,
  addHotelService,
  addHotelProduct,
  updateHotelService,
  updateHotelProduct,
  deleteHotelService,
  deleteHotelProduct,
} from "../../../FIREBASECONFIG";
import { fetchHotels } from "../../../api/resort-booking/resort.service";

import Loader from "../utilities/Loader";

import "bootstrap/dist/css/bootstrap.min.css";
import "tabulator-tables/dist/css/tabulator_bootstrap5.min.css";

// This component mirrors RoomsInventory styling and Tabulator usage
export default function HotelServicesProducts() {
  const userDetails = JSON.parse(sessionStorage.getItem("userDetails") || "{}");
  const userHotelId = userDetails.hotelId
  // ||
  // (userDetails.city && userDetails.city !== "*" ? userDetails.city : "") ||
  // "";
  const hotelDropdownLocked = Boolean(userHotelId);

  const tableRef = useRef(null);
  const tabulatorRef = useRef(null);
  const tableRefProducts = useRef(null);
  const tabulatorRefProducts = useRef(null);

  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState("");

  const hotelsForDropdown = useMemo(() => {
    if (!hotelDropdownLocked) return hotels;
    const match = hotels.filter((h) => h.id === userHotelId);
    if (match.length) return match;
    return [{ id: userHotelId, name: userHotelId }];
  }, [hotels, hotelDropdownLocked, userHotelId]);

  const [servicesDataSet, setServicesDataSet] = useState([]);
  const [productsDataSet, setProductsDataSet] = useState([]);

  const [loading, setLoading] = useState(false);

  // Modal states
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [serviceForm, setServiceForm] = useState({
    id: "",
    name: "",
    description: "",
    image: "",
    active: true,
  });

  const [showProductModal, setShowProductModal] = useState(false);
  const [productForm, setProductForm] = useState({
    id: "",
    name: "",
    desc: "",
    price: 0,
    typeId: "",
    displayId: "",
    image: "",
    active: true,
  });

  const [serviceImageFile, setServiceImageFile] = useState(null);
  const [productImageFile, setProductImageFile] = useState(null);

  // selected tab: 'services' or 'products'
  const [activeTab, setActiveTab] = useState("services");

  useEffect(() => {
    (async () => {
      const h = await fetchHotels();
      setHotels(h || []);
      if (userHotelId) {
        setSelectedHotel(userHotelId);
      } else {
        const sessionHotel = sessionStorage.getItem("id");
        if (sessionHotel && h.find((x) => x.id === sessionHotel)) {
          setSelectedHotel(sessionHotel);
        }
      }
    })();
  }, [userHotelId]);

  useEffect(() => {
    if (!selectedHotel) return;
    loadServices();
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHotel]);

  const loadServices = async () => {
    try {
      setLoading(true);
      const s = await getHotelServices(selectedHotel);
      // normalize fields: services helper may use different field names; map to name/description
      const normalized = (s || []).map((it) => ({
        id: it.id,
        name: it.type || it.name || it.typeName || "Untitled",
        description: it.desc || it.description || "",
        image: it.image || "",
        active: it.active ?? true,
      }));
      setServicesDataSet(normalized);
    } catch (err) {
      console.error("loadServices", err);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      const p = await getHotelProducts(selectedHotel);
      // normalize product fields
      const normalized = (p || []).map((it) => ({
        id: it.id,
        name: it.name || it.productName || "Untitled",
        desc: it.desc || it.description || "",
        price: it.price ?? 0,
        typeId: it.typeId || it.categoryId || "",
        displayId: it.displayId || it.id,
        image: it.image || "",
        active: it.active ?? true,
      }));
      setProductsDataSet(normalized);
    } catch (err) {
      console.error("loadProducts", err);
    } finally {
      setLoading(false);
    }
  };

  // ------------------ Tabulator for Services ------------------
  useEffect(() => {
    if (!tableRef.current) return;

    if (tabulatorRef.current) {
      try {
        tabulatorRef.current.destroy();
      } catch (e) {}
      tabulatorRef.current = null;
    }

    const columns = [
      {
        title: "Image",
        field: "image",
        headerSort: false,
        // hozAlign: "center",
        formatter: (cell) => {
          const v = cell.getValue();
          return v
            ? `<img src='${v}' style='width:48px;height:48px;object-fit:cover;border-radius:6px'/>`
            : '<div style="width:48px;height:48px;display:flex;align-items:center;justify-content:center;background:#f3f3f3;border-radius:6px;">No</div>';
        },
      },
      { title: "Category", field: "name", headerSort: false },
      {
        title: "Description",
        field: "description",
        headerSort: false,
        formatter: "textarea",
      },
      {
        title: "Active",
        field: "active",
        headerSort: false,
        // hozAlign: "center",
        formatter: function (cell) {
          return cell.getValue()
            ? '<span class="badge bg-success">Active</span>'
            : '<span class="badge bg-secondary">Inactive</span>';
        },
        cellClick: async (e, cell) => {
          const d = cell.getRow().getData();
          // toggle using helper
          try {
            await updateHotelService(
              selectedHotel,
              d.id,
              { type: d.name, desc: d.description, active: !d.active },
              null
            );
            loadServices();
          } catch (err) {
            console.error(err);
          }
        },
      },
      {
        title: "Actions",
        headerSort: false,
        // hozAlign: "center",
        formatter: (cell) =>
          '<i class="bi bi-pencil-square table-icon me-2" style="cursor:pointer"></i><i class="bi bi-trash table-icon text-danger" style="cursor:pointer"></i>',
        cellClick: (e, cell) => {
          const el = e.target;
          const row = cell.getRow().getData();
          if (el.classList.contains("bi-trash")) {
            // delete
            if (!window.confirm("Delete category and its products?")) return;
            deleteHotelService(selectedHotel, row.id)
              .then(() => {
                loadServices();
                loadProducts();
              })
              .catch(console.error);
          } else {
            // edit
            setServiceForm({
              id: row.id,
              name: row.name,
              description: row.description,
              image: row.image,
              active: row.active,
            });
            setServiceImageFile(null);
            setShowServiceModal(true);
          }
        },
      },
    ];

    tabulatorRef.current = new Tabulator(tableRef.current, {
      layout: "fitColumns",
      data: servicesDataSet,
      columns,
      placeholder: "No categories found",
      pagination: "local",
      paginationSize: 50,
      height: "400px",
    });

    return () => {
      if (tabulatorRef.current) {
        try {
          tabulatorRef.current.destroy();
        } catch (e) {}
        tabulatorRef.current = null;
      }
    };
  }, [servicesDataSet, selectedHotel]);

  // ------------------ Tabulator for Products ------------------
  useEffect(() => {
    if (!tableRefProducts.current) return;

    if (tabulatorRefProducts.current) {
      try {
        tabulatorRefProducts.current.destroy();
      } catch (e) {}
      tabulatorRefProducts.current = null;
    }

    const svcMap = servicesDataSet.reduce(
      (acc, s) => ({ ...acc, [s.id]: s.name }),
      {}
    );

    const columns = [
      {
        title: "Image",
        field: "image",
        headerSort: false,
        // hozAlign: "center",
        formatter: (cell) => {
          const v = cell.getValue();
          return v
            ? `<img src='${v}' style='width:48px;height:48px;object-fit:cover;border-radius:6px'/>`
            : '<div style="width:48px;height:48px;display:flex;align-items:center;justify-content:center;background:#f3f3f3;border-radius:6px;">No</div>';
        },
      },
      { title: "Product", field: "name", headerSort: false },
      {
        title: "Category",
        field: "typeId",
        headerSort: false,
        formatter: (cell) => svcMap[cell.getValue()] || "(Unknown)",
      },
      {
        title: "Price",
        field: "price",
        headerSort: false,
        // hozAlign: "right",
        formatter: (cell) => Number(cell.getValue() || 0).toLocaleString(),
      },
      {
        title: "Active",
        field: "active",
        headerSort: false,
        // hozAlign: "center",
        formatter: function (cell) {
          return cell.getValue()
            ? '<span class="badge bg-success">Active</span>'
            : '<span class="badge bg-secondary">Inactive</span>';
        },
        cellClick: async (e, cell) => {
          const d = cell.getRow().getData();
          try {
            await updateHotelProduct(
              selectedHotel,
              d.id,
              {
                name: d.name,
                desc: d.desc,
                price: d.price,
                typeId: d.typeId,
                active: !d.active,
              },
              null
            );
            loadProducts();
          } catch (err) {
            console.error(err);
          }
        },
      },
      {
        title: "Actions",
        headerSort: false,
        // hozAlign: "center",
        formatter: (cell) =>
          '<i class="bi bi-pencil-square table-icon me-2" style="cursor:pointer"></i><i class="bi bi-trash table-icon text-danger" style="cursor:pointer"></i>',
        cellClick: (e, cell) => {
          const el = e.target;
          const row = cell.getRow().getData();
          if (el.classList.contains("bi-trash")) {
            if (!window.confirm("Delete product?")) return;
            deleteHotelProduct(selectedHotel, row.id)
              .then(() => loadProducts())
              .catch(console.error);
          } else {
            // edit
            setProductForm({
              id: row.id,
              name: row.name,
              desc: row.desc,
              price: row.price,
              typeId: row.typeId,
              displayId: row.displayId,
              image: row.image,
              active: row.active,
            });
            setProductImageFile(null);
            setShowProductModal(true);
          }
        },
      },
    ];

    tabulatorRefProducts.current = new Tabulator(tableRefProducts.current, {
      layout: "fitColumns",
      data: productsDataSet,
      columns,
      placeholder: "No products found",
      pagination: "local",
      paginationSize: 50,
      height: "500px",
      groupBy: "typeId",
      groupHeader: (value, count, data, group) =>
        `${
          svcMap[value] || value
        } <span style='margin-left:8px;'>(${count} items)</span>`,
    });

    return () => {
      if (tabulatorRefProducts.current) {
        try {
          tabulatorRefProducts.current.destroy();
        } catch (e) {}
        tabulatorRefProducts.current = null;
      }
    };
  }, [productsDataSet, servicesDataSet, selectedHotel]);

  // ------------------ Handlers ------------------
  const onAddCategoryClick = () => {
    setServiceForm({
      id: "",
      name: "",
      description: "",
      image: "",
      active: true,
    });
    setServiceImageFile(null);
    setShowServiceModal(true);
    setActiveTab("services");
  };

  const onAddProductClick = () => {
    setProductForm({
      id: "",
      name: "",
      desc: "",
      price: 0,
      typeId: servicesDataSet[0]?.id || "",
      displayId: "PR-" + Math.random().toString(36).slice(2, 8).toUpperCase(),
      image: "",
      active: true,
    });
    setProductImageFile(null);
    setShowProductModal(true);
    setActiveTab("products");
  };

  const saveService = async () => {
    if (!serviceForm.name) return alert("Please provide category name");
    try {
      if (serviceForm.id) {
        await updateHotelService(
          selectedHotel,
          serviceForm.id,
          {
            type: serviceForm.name,
            desc: serviceForm.description,
            active: serviceForm.active,
          },
          serviceImageFile
        );
      } else {
        await addHotelService(
          selectedHotel,
          {
            type: serviceForm.name,
            desc: serviceForm.description,
            active: serviceForm.active,
          },
          serviceImageFile
        );
      }
      setShowServiceModal(false);
      await loadServices();
      await loadProducts();
    } catch (err) {
      console.error(err);
      alert("Failed to save category");
    }
  };

  const saveProduct = async () => {
    if (!productForm.name) return alert("Please provide product name");
    if (!productForm.typeId) return alert("Please select category");
    try {
      const payload = {
        name: productForm.name,
        desc: productForm.desc,
        price: Number(
          productForm.price || productForm.price === 0
            ? productForm.price
            : productForm.price
        ),
        typeId: productForm.typeId,
        displayId: productForm.displayId,
        active: productForm.active,
      };
      if (productForm.id) {
        await updateHotelProduct(
          selectedHotel,
          productForm.id,
          payload,
          productImageFile
        );
      } else {
        await addHotelProduct(selectedHotel, payload, productImageFile);
      }
      setShowProductModal(false);
      await loadProducts();
    } catch (err) {
      console.error(err);
      alert("Failed to save product");
    }
  };

  return (
    <div style={{ position: "relative" }} className="p-3">
      {loading && <Loader />}

      {/* Top row: hotel select + add buttons */}
      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "services" ? "active" : ""}`}
            onClick={() => setActiveTab("services")}
          >
            Categories
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "products" ? "active" : ""}`}
            onClick={() => setActiveTab("products")}
          >
            Products
          </button>
        </li>
      </ul>
      <div className="card">
        <div className=" card-header d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            {/* <label className="mb-0 me-2">Hotel</label> */}
            <select
              className="form-select"
              style={{ width: 300 }}
              value={selectedHotel}
              onChange={(e) => setSelectedHotel(e.target.value)}
              disabled={hotelDropdownLocked}
              title={
                hotelDropdownLocked
                  ? "Your account is limited to this hotel"
                  : undefined
              }
            >
              {!hotelDropdownLocked && (
                <option value="">-- Select Hotel --</option>
              )}
              {hotelsForDropdown.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name || h.title || h.id}
                </option>
              ))}
            </select>
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn btn-primary"
              onClick={onAddCategoryClick}
              disabled={!selectedHotel}
            >
              + Add Category
            </button>
            <button
              className="btn btn-primary"
              onClick={onAddProductClick}
              disabled={!selectedHotel || servicesDataSet.length === 0}
            >
              + Add Product
            </button>
          </div>
        </div>

        {/* Tables */}
        <div style={{ display: activeTab === "services" ? "block" : "none" }}>
          <div id="services-table" ref={tableRef} style={{ minHeight: 200 }} />
        </div>

        <div style={{ display: activeTab === "products" ? "block" : "none" }}>
          <div
            id="products-table"
            ref={tableRefProducts}
            style={{ minHeight: 300 }}
          />
        </div>

        {/* Service Modal */}
        {showServiceModal && (
          <>
            <div
              className="modal fade show"
              tabIndex={-1}
              style={{ display: "block", background: "rgba(0,0,0,0.4)" }}
            >
              <div className="modal-dialog modal-md modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {serviceForm.id ? "Edit Category" : "Add Category"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowServiceModal(false)}
                    />
                  </div>
                  <div className="modal-body">
                    <div className="mb-2">
                      <label className="form-label">Name</label>
                      <input
                        className="form-control"
                        value={serviceForm.name}
                        onChange={(e) =>
                          setServiceForm({
                            ...serviceForm,
                            name: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        value={serviceForm.description}
                        onChange={(e) =>
                          setServiceForm({
                            ...serviceForm,
                            description: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label">Image (optional)</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={(e) =>
                          setServiceImageFile(e.target.files?.[0] || null)
                        }
                      />
                      {serviceForm.image && (
                        <img
                          src={serviceForm.image}
                          alt="preview"
                          style={{
                            width: 120,
                            height: 80,
                            objectFit: "cover",
                            marginTop: 8,
                          }}
                        />
                      )}
                    </div>
                    <div className="form-check form-switch mt-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={serviceForm.active}
                        onChange={(e) =>
                          setServiceForm({
                            ...serviceForm,
                            active: e.target.checked,
                          })
                        }
                        id="svcActive"
                      />
                      <label className="form-check-label" htmlFor="svcActive">
                        Active
                      </label>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowServiceModal(false)}
                    >
                      Close
                    </button>
                    <button className="btn btn-primary" onClick={saveService}>
                      {serviceForm.id ? "Update" : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show" />
          </>
        )}

        {/* Product Modal */}
        {showProductModal && (
          <>
            <div
              className="modal fade show"
              tabIndex={-1}
              style={{ display: "block", background: "rgba(0,0,0,0.4)" }}
            >
              <div className="modal-dialog modal-md modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {productForm.id ? "Edit Product" : "Add Product"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowProductModal(false)}
                    />
                  </div>
                  <div className="modal-body">
                    <div className="d-flex gap-2">
                      <div className="mb-2">
                        <label className="form-label">Product Name</label>
                        <input
                          className="form-control"
                          value={productForm.name}
                          onChange={(e) =>
                            setProductForm({
                              ...productForm,
                              name: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Category</label>
                        <select
                          className="form-select"
                          value={productForm.typeId}
                          onChange={(e) =>
                            setProductForm({
                              ...productForm,
                              typeId: e.target.value,
                            })
                          }
                        >
                          <option value="">-- Select Category --</option>
                          {servicesDataSet.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mb-2">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        value={productForm.desc}
                        onChange={(e) =>
                          setProductForm({
                            ...productForm,
                            desc: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="d-flex gap-2">
                      <div className="mb-2">
                        <label className="form-label">Price</label>
                        <input
                          type="number"
                          className="form-control"
                          value={productForm.price}
                          onChange={(e) =>
                            setProductForm({
                              ...productForm,
                              price: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Image (optional)</label>
                        <input
                          type="file"
                          className="form-control"
                          onChange={(e) =>
                            setProductImageFile(e.target.files?.[0] || null)
                          }
                        />
                        {productForm.image && (
                          <img
                            src={productForm.image}
                            alt="preview"
                            style={{
                              width: 120,
                              height: 80,
                              objectFit: "cover",
                              marginTop: 8,
                            }}
                          />
                        )}
                      </div>
                    </div>

                    <div className="form-check form-switch mt-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={productForm.active}
                        onChange={(e) =>
                          setProductForm({
                            ...productForm,
                            active: e.target.checked,
                          })
                        }
                        id="prdActive"
                      />
                      <label className="form-check-label" htmlFor="prdActive">
                        Active
                      </label>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowProductModal(false)}
                    >
                      Close
                    </button>
                    <button className="btn btn-primary" onClick={saveProduct}>
                      {productForm.id ? "Update" : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show" />
          </>
        )}
      </div>
    </div>
  );
}
